/**
 * Tidsöverföring mellan stegen i en rutin.
 *
 * Blir ett steg klart i förtid läggs tiden som blev över till NÄSTA steg; drar ett
 * steg över tiden dras överdraget av från nästa. Rutinen håller alltså ihop sin
 * totala längd i stället för att glida framåt hela morgonen — det är den som är
 * inplanerad mot resten av dagen.
 *
 * Modellen är EN siffra per rutininstans, `carry` i sekunder:
 *
 *   budget(steg)   = stegets egen tid + carry            (kan vara NEGATIV)
 *   visad tid      = max(0, budget)                      (en timer räknar inte minus)
 *   nästa carry    = återstående tid + min(0, budget)
 *
 * Den sista raden är hela poängen med att budgeten sparas OKLAMPAD: är överdraget
 * större än nästa steg är, tar det steget slut direkt och resten förs vidare till
 * steget därpå i stället för att tyst försvinna. Positiv carry = tid till godo.
 *
 * Läget ligger i localStorage per instans, vid sidan av timerstaten (`timer-<id>`)
 * som useTimerState redan äger. Det behöver inte till servern: carry gäller EN
 * genomgång av rutinen på den enhet den görs, och nollställs när rutinen är klar.
 *
 * Lagringen är en STACK och inte ett enda tal, just för att Tillbaka-knappen ska bli
 * sann: varje Nästa lägger sitt nya carry överst, och ett steg tillbaka plockar bort
 * det igen. Vore det ett enda tal hade det steg man backar till fått räkna sitt eget
 * överskott en andra gång.
 */

/** Timerstaten som useTimerState sparar. Samma form som där, läst men aldrig skriven. */
type StoredTimerState = {
  remainingTime: number;
  paused: boolean;
  estimatedEndTime: number;
};

const carryKey = (activityInstanceId: number): string => `routine-carry-${activityInstanceId}`;

/** Ett carry per avklarat steg, senast överst. Tom lista = rutinen ligger i fas. */
export type CarryStack = number[];

export const readCarryStack = (activityInstanceId: number): CarryStack => {
  const stored = localStorage.getItem(carryKey(activityInstanceId));
  if (!stored) return [];
  try {
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((value): value is number => typeof value === 'number' && isFinite(value));
  } catch {
    return [];
  }
};

export const writeCarryStack = (activityInstanceId: number, stack: CarryStack): void => {
  localStorage.setItem(
    carryKey(activityInstanceId),
    JSON.stringify(stack.map((value) => Math.round(value))),
  );
};

export const clearCarry = (activityInstanceId: number): void => {
  localStorage.removeItem(carryKey(activityInstanceId));
};

/** Det carry som gäller för det steg brukaren står på nu. */
export const currentCarrySeconds = (stack: CarryStack): number =>
  stack.length > 0 ? stack[stack.length - 1] : 0;

/** Stegets tid plus det som bars över. Oklampad — negativ betyder skuld. */
export const getStepBudgetSeconds = (durationMinutes: number, carrySeconds: number): number =>
  durationMinutes * 60 + carrySeconds;

/** Det timern faktiskt ska räkna ner från. En skuld visas som 0, inte som minustid. */
export const getShownSeconds = (budgetSeconds: number): number => Math.max(0, budgetSeconds);

/**
 * Återstående sekunder för ett steg, ur den sparade timerstaten.
 *
 * NEGATIVT värde = steget har dragit över tiden. Det är hela skälet till att tiden
 * räknas ut här ur `estimatedEndTime` i stället för att läsas ur timerkomponenten:
 * useTimerState stannar på 0 och kan därför inte berätta HUR MYCKET över man gick.
 * Pausad timer läses som den stod när den pausades — pausad tid är inte förbrukad.
 */
export const readRemainingSeconds = (timerKey: string, fallbackSeconds: number): number => {
  const stored = localStorage.getItem(`timer-${timerKey}`);
  if (!stored) return fallbackSeconds;
  try {
    const state = JSON.parse(stored) as StoredTimerState;
    const remaining = state.paused
      ? Math.round(state.remainingTime)
      : Math.round((state.estimatedEndTime - Date.now()) / 1000);
    // Ett tal som inte är ett tal (fel form i localStorage) skulle annars förgifta
    // hela carry-kedjan: NaN i budgeten ger NaN i varje efterföljande steg.
    return isFinite(remaining) ? remaining : fallbackSeconds;
  } catch {
    // Trasigt värde i localStorage ska inte stoppa rutinen: fall tillbaka på att
    // steget tog exakt sin tid, alltså ingen ny över- eller underskottstid.
    return fallbackSeconds;
  }
};

/**
 * Största SKULD som får bäras vidare till nästa steg (sekunder).
 *
 * Utan tak är carryn obegränsad nedåt, och det är inte en teoretisk kant: lämnar
 * brukaren rutinen igång och kommer tillbaka efter några timmar blir `remaining`
 * kraftigt negativt, skulden växer för varje steg, och getShownSeconds ger 0 för
 * ALLA återstående steg. Rutinen visar då 0:00 direkt på varje steg och enda vägen
 * ut är Tillbaka-knappen ett steg i taget — precis motsatsen till det stöd rutinen
 * ska ge.
 *
 * Taket tar INTE bort carryn. Ett verkligt överdrag — några sekunder till några
 * minuter — ligger med god marginal under gränsen och förs vidare precis som förut;
 * det är hela poängen med modellen. Gränsen skiljer bara på två olika saker som ser
 * likadana ut i siffrorna: ett steg som TOG för lång tid, och en rutin som STÅTT
 * STILLA. Tio minuter ligger över varje rimligt överdrag inom ett steg och under
 * den tidsrymd där ingen längre är närvarande.
 *
 * Effekten är att skulden dräneras i stället för att växa: en glömd rutin kostar
 * som mest de närmaste ~10 minuterna av stegen, och därefter får resten av rutinen
 * sin fulla tid igen.
 */
export const MAX_CARRY_DEBT_SECONDS = 10 * 60;

/** Överskottet/underskottet som ska bäras vidare när steget lämnas. */
export const nextCarrySeconds = (remainingSeconds: number, budgetSeconds: number): number =>
  Math.max(-MAX_CARRY_DEBT_SECONDS, remainingSeconds + Math.min(0, budgetSeconds));
